/**
 * CameraPalette — the edit-mode rail of cameras waiting to be pinned.
 *
 * Cameras already placed on ANOTHER layout are listed but disabled, labelled with the
 * layout that holds them. Showing them (rather than hiding them) is deliberate: a camera
 * belongs to exactly one layout, and an operator hunting for "CAM-12" should learn where it
 * went instead of concluding it doesn't exist.
 */
import { useMemo, useState } from 'react';
import { MagnifyingGlassIcon, VideoCameraIcon } from '@heroicons/react/24/outline';
import type { HexCamera, MapPlacedCamera } from '../../api/contracts';

export interface CameraPaletteProps {
  cameras: HexCamera[];
  /** Cameras already pinned on the layout being edited — hidden from the palette. */
  placedHere: Set<string>;
  /** cameraId → the layout that owns it, across all layouts. */
  placedElsewhere: Record<string, MapPlacedCamera>;
  /** The camera armed for the next click on the plan. */
  armedId: string | null;
  onArm: (cameraId: string | null) => void;
}

export default function CameraPalette({
  cameras,
  placedHere,
  placedElsewhere,
  armedId,
  onArm,
}: CameraPaletteProps) {
  const [q, setQ] = useState('');

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return cameras
      .filter((c) => !placedHere.has(c.id))
      .filter((c) => !needle || c.name.toLowerCase().includes(needle))
      .map((c) => ({ cam: c, taken: placedElsewhere[c.id] }))
      // Available first — the list is for placing cameras, not for browsing taken ones.
      .sort((a, b) => {
        if (!!a.taken !== !!b.taken) return a.taken ? 1 : -1;
        return a.cam.name.localeCompare(b.cam.name);
      });
  }, [cameras, placedHere, placedElsewhere, q]);

  const available = rows.filter((r) => !r.taken).length;

  return (
    <div className="flex min-h-0 flex-col gap-2">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Cameras</h3>
        <span className="text-[10px] text-muted-foreground tabular-nums">{available} available</span>
      </div>

      <div className="relative">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search cameras"
          className="h-8 w-full rounded-lg border border-border bg-input pl-7 pr-2 text-xs text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand-ring"
        />
      </div>

      <p className="text-[10px] leading-tight text-muted-foreground">
        Pick a camera, then click the plan to place it. Drag a pin to move it.
      </p>

      <div className="min-h-0 flex-1 space-y-1 overflow-y-auto pr-0.5">
        {rows.length === 0 && (
          <p className="py-4 text-center text-xs text-muted-foreground">
            {q ? 'No matching cameras.' : 'Every camera is already placed.'}
          </p>
        )}
        {rows.map(({ cam, taken }) => {
          const armed = armedId === cam.id;
          return (
            <button
              key={cam.id}
              type="button"
              disabled={!!taken}
              onClick={() => onArm(armed ? null : cam.id)}
              title={taken ? `Already placed on "${taken.layout_name}"` : cam.name}
              className={`flex w-full items-center gap-2 rounded-md border px-2 py-1.5 text-left text-xs transition-colors ${
                taken
                  ? 'cursor-not-allowed border-border/50 bg-muted/40 text-muted-foreground'
                  : armed
                    ? 'border-brand bg-brand-light text-foreground'
                    : 'border-border bg-card text-foreground hover:bg-muted'
              }`}
            >
              <VideoCameraIcon className="h-3.5 w-3.5 flex-none" />
              <span className="min-w-0 flex-1 truncate">{cam.name}</span>
              {taken && <span className="flex-none truncate text-[10px] italic">on {taken.layout_name}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}
